import { Injectable } from '@angular/core';
import { Observable, from, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { User, ModelOnboardingState, ClientOnboardingState } from '../models/user';
import { Model } from '../models/model';
import { Client } from '../models/client';
import { 
  Firestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  CollectionReference, 
  DocumentData,
  serverTimestamp
} from '@angular/fire/firestore';


@Injectable({
  providedIn: 'root'
})
export class UserService {
  private usersCollection: CollectionReference<DocumentData>;

  constructor(private firestore: Firestore) {
    this.usersCollection = collection(this.firestore, 'users');
  }

  getUsers(): Observable<User[]> {
    // Returns all users as an observable array
    return from(getDocs(this.usersCollection)).pipe(
      map((snapshot: any) => snapshot.docs.map((docSnap: any) => User.fromFirestore({ ...docSnap.data(), id: docSnap.id })))
    );
  }

  getUserById(id: string): Observable<User | null> {
    const userDoc = doc(this.usersCollection, id);
    return from(getDoc(userDoc)).pipe(
      map((docSnap: any) => docSnap.exists() ? User.fromFirestore({ ...docSnap.data(), id: docSnap.id }) : null)
    );
  }

  createUser(user: User): Observable<User> {
    const userDoc = doc(this.usersCollection, user.user_id);
    return from(setDoc(userDoc, user.toFirestore())).pipe(
      map(() => user)
    );
  }

  updateUser(user: User): Observable<User> {
    const userDoc = doc(this.usersCollection, user.user_id);
    return from(updateDoc(userDoc, { ...user })).pipe(
      map(() => user)
    );
  }

  deleteUser(id: string): Observable<void> {
    const userDoc = doc(this.usersCollection, id);
    return from(deleteDoc(userDoc));
  }

  /**
   * Initializes a user profile after registration with auto-profile creation.
   * - If type=model query param → creates MODEL profile
   * - If type=client query param or no type → creates CLIENT profile
   * @param userData Partial user data from registration response
   * @param displayName Display name from registration form
   */
  initializeUserProfile(userData: Partial<User>, displayName: string): Observable<{ user: User; isModel: boolean }> {
    const referralCode = localStorage.getItem('referralCode');
    const accountType = localStorage.getItem('accountType');
    const isModel = accountType === 'model';
    const uid = userData.user_id || '';
    const email = userData.email || '';

    const defaultUser: User = new User({
      user_id: uid,
      email: email || null,
      display_name: displayName,
      is_verified: userData.is_verified ?? false,
      has_profile: true, // Profile auto-created
      is_model: isModel,
      is_admin: userData.is_admin ?? false,
      referral_code: referralCode ?? null,
      referredBy: referralCode ?? null,
      // Explicit onboarding state prevents backward-compat fallback from
      // treating a brand-new minimal profile as "complete"
      onboarding: isModel
        ? { model: { completed: false, acknowledged: false } }
        : { client: { completed: false, acknowledged: false } }
    });

    // Create user document first
    return this.createUser(defaultUser).pipe(
      switchMap(user => {
        if (isModel) {
          // Create minimal model profile
          return this.createMinimalModel(uid, email, displayName).pipe(
            map(() => ({ user, isModel: true }))
          );
        } else {
          // Create minimal client profile
          return this.createMinimalClient(uid, email, displayName).pipe(
            map(() => ({ user, isModel: false }))
          );
        }
      })
    );
  }

  /**
   * Creates a minimal model document for referral-based registrations
   */
  private createMinimalModel(uid: string, email: string, displayName: string): Observable<void> {
    const modelsCollection = collection(this.firestore, 'models');
    const modelDoc = doc(modelsCollection, uid);
    
    const minimalModel: Partial<Model> = {
      model_id: uid,
      user_id: uid,
      name: displayName,
      email: email,
      description: null,
      date_of_birth: new Date(),
      age: null,
      gender: '',
      phone_number: null,
      profile_image_url: null,
      profile_banner_url: null,
      twitter_url: null,
      instagram_url: null,
      city: null,
      province: null,
      country: null,
      tags: '',
      likesCount: 0,
      viewsCount: 0,
      imagesCount: 0,
      videosCount: 0
    };

    return from(setDoc(modelDoc, minimalModel)).pipe(map(() => undefined));
  }

  /**
   * Creates a minimal client document for non-referral registrations
   */
  private createMinimalClient(uid: string, email: string, displayName: string): Observable<void> {
    const clientsCollection = collection(this.firestore, 'clients');
    const clientDoc = doc(clientsCollection, uid);
    
    const minimalClient: Partial<Client> = {
      client_id: uid,
      user_id: uid,
      name: displayName,
      email: email,
      date_of_birth: new Date(),
      age: '',
      gender: '',
      phone_number: '',
      profile_image_url: ''
    };

    return from(setDoc(clientDoc, minimalClient)).pipe(map(() => undefined));
  }

  /**
   * Updates model onboarding state with merge to preserve other fields.
   * Sets completed=true, acknowledged=false, and completedAt timestamp.
   */
  setModelOnboardingComplete(userId: string): Observable<void> {
    const userDoc = doc(this.usersCollection, userId);
    const update = {
      'onboarding.model.completed': true,
      'onboarding.model.acknowledged': false,
      'onboarding.model.completedAt': serverTimestamp()
    };
    return from(updateDoc(userDoc, update)).pipe(map(() => undefined));
  }

  /**
   * Acknowledges model onboarding banner (user dismissed it).
   * Uses dot notation to merge safely without overwriting other fields.
   */
  acknowledgeModelOnboarding(userId: string): Observable<void> {
    const userDoc = doc(this.usersCollection, userId);
    const update = {
      'onboarding.model.acknowledged': true
    };
    return from(updateDoc(userDoc, update)).pipe(map(() => undefined));
  }

  /**
   * Updates client onboarding state with merge to preserve other fields.
   */
  setClientOnboardingComplete(userId: string): Observable<void> {
    const userDoc = doc(this.usersCollection, userId);
    const update = {
      'onboarding.client.completed': true,
      'onboarding.client.acknowledged': false
    };
    return from(updateDoc(userDoc, update)).pipe(map(() => undefined));
  }

  /**
   * Acknowledges client onboarding banner.
   */
  acknowledgeClientOnboarding(userId: string): Observable<void> {
    const userDoc = doc(this.usersCollection, userId);
    const update = {
      'onboarding.client.acknowledged': true
    };
    return from(updateDoc(userDoc, update)).pipe(map(() => undefined));
  }

  /**
   * Upgrades a client user to a model.
   * 1. Creates a model document using relevant client fields.
   * 2. Sets is_model = true on the user document.
   * Reuses the same model document shape as createMinimalModel for consistency.
   * Client record is intentionally preserved to avoid breaking references.
   */
  upgradeClientToModel(client: Client): Observable<void> {
    const uid = client.user_id;

    // Map client fields → model document (explicit, no extra/conflicting fields)
    const modelData: Partial<Model> = {
      model_id: uid,
      user_id: uid,
      name: client.name,
      email: client.email,
      date_of_birth: client.date_of_birth,
      age: client.age || null,
      gender: client.gender,
      phone_number: client.phone_number || null,
      profile_image_url: client.profile_image_url || null,
      description: null,
      profile_banner_url: null,
      twitter_url: null,
      instagram_url: null,
      city: null,
      province: null,
      country: null,
      tags: '',
      likesCount: 0,
      viewsCount: 0,
      imagesCount: 0,
      videosCount: 0
    };

    const modelsCollection = collection(this.firestore, 'models');
    const modelDoc = doc(modelsCollection, uid);

    // Create model doc, then update user flags + initialize model onboarding
    return from(setDoc(modelDoc, modelData)).pipe(
      switchMap(() => {
        const userDoc = doc(this.usersCollection, uid);
        // Set is_model and explicitly initialize onboarding.model so the
        // checklist appears (prevents backward-compat fallback from treating
        // has_profile + is_model as "already completed").
        return from(updateDoc(userDoc, {
          is_model: true,
          'onboarding.model.completed': false,
          'onboarding.model.acknowledged': false
        }));
      }),
      map(() => undefined)
    );
  }
}